/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@store/auth.store';
import Layout from '@components/layout/Layout';
import RouteOutlet from '@components/layout/RouteOutlet';

// The cold entry path stays static: a visitor who is not signed in needs the
// login screen and the MSAL callback in the first download, and making either
// lazy would add a round trip to the very screen this split exists to speed up.
import LoginPage from '@pages/Login';
import AuthCallback from '@pages/AuthCallback';

// Every page behind the sign-in is its own chunk, fetched the first time its
// route renders (code-health survey 10-performance #4, 17-wasted-work #5:
// before this the sign-in screen downloaded and parsed the whole application,
// react-markdown, DOMPurify and every admin page included). They suspend into
// the single boundary in RouteOutlet, which also owns the stale-deploy reload.
// All pages are default exports; a page that becomes a named export goes
// through `.then((m) => ({ default: m.X }))` here, not by editing the page.
const ChatPage = lazy(() => import('@pages/Chat'));
const CoursesPage = lazy(() => import('@pages/Courses'));
const AdminCourseDetailPage = lazy(() => import('@pages/courses/CourseDetail'));
const AdminDashboardPage = lazy(() => import('@pages/AdminDashboard'));
const UsersGroupsPage = lazy(() => import('@pages/UsersGroups'));
const GroupDetailPage = lazy(() => import('@pages/GroupDetail'));
const DocPage = lazy(() => import('@pages/Doc'));
const ApiDocsPage = lazy(() => import('@pages/ApiDocs'));
const SettingsPage = lazy(() => import('@pages/Settings'));

// Learner pages
const CatalogPage = lazy(() => import('@pages/learn/Catalog'));
const CourseDetailPage = lazy(() => import('@pages/learn/CourseDetail'));
const LearnPage = lazy(() => import('@pages/learn/Learn'));
const QuizPage = lazy(() => import('@pages/learn/Quiz'));
const ProfilePage = lazy(() => import('@pages/learn/Profile'));
const CertificatesPage = lazy(() => import('@pages/learn/Certificates'));
const DashboardPage = lazy(() => import('@pages/learn/Dashboard'));

// AI Builder pages (admin-only, under /learn/ai-builder for legacy URL compat)
const AIBuilderPage = lazy(() => import('@pages/learn/AIBuilder'));
const AIBuilderCoursePage = lazy(() => import('@pages/learn/AIBuilderCourse'));
const AIBuilderLessonPage = lazy(() => import('@pages/learn/AIBuilderLesson'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AdminOnly({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();
  if (user?.role === 'learner') return <Navigate to="/learn" replace />;
  return <>{children}</>;
}

function RootRedirect() {
  const { user } = useAuthStore();
  return <Navigate to={user?.role === 'learner' ? '/learn/dashboard' : '/dashboard'} replace />;
}

function adminRoutes() {
  return [
    <Route
      key="chat"
      path="chat"
      element={
        <AdminOnly>
          <ChatPage />
        </AdminOnly>
      }
    />,
    <Route
      key="courses"
      path="courses"
      element={
        <AdminOnly>
          <CoursesPage />
        </AdminOnly>
      }
    />,
    <Route
      key="courseDetail"
      path="courses/:courseId"
      element={
        <AdminOnly>
          <AdminCourseDetailPage />
        </AdminOnly>
      }
    />,
    <Route
      key="usersGroups"
      path="users-groups"
      element={
        <AdminOnly>
          <UsersGroupsPage />
        </AdminOnly>
      }
    />,
    <Route
      key="usersRedirect"
      path="users"
      element={<Navigate to="/users-groups?tab=users" replace />}
    />,
    <Route
      key="groupsRedirect"
      path="groups"
      element={<Navigate to="/users-groups?tab=groups" replace />}
    />,
    <Route
      key="dashboard"
      path="dashboard"
      element={
        <AdminOnly>
          <AdminDashboardPage />
        </AdminOnly>
      }
    />,
    <Route key="analytics" path="analytics" element={<Navigate to="/dashboard" replace />} />,
    <Route
      key="groupDetail"
      path="groups/:groupId"
      element={
        <AdminOnly>
          <GroupDetailPage />
        </AdminOnly>
      }
    />,
    <Route
      key="settings"
      path="settings"
      element={
        <AdminOnly>
          <SettingsPage />
        </AdminOnly>
      }
    />,
    <Route key="doc" path="doc" element={<DocPage />} />,
    <Route
      key="apiDocs"
      path="api-docs"
      element={
        <AdminOnly>
          <ApiDocsPage />
        </AdminOnly>
      }
    />,
  ];
}

function aiBuilderRoutes() {
  return [
    /* AI Builder — admin-only (lives under /learn/ for legacy URL compat) */
    <Route
      key="aiBuilder"
      path="learn/ai-builder"
      element={
        <AdminOnly>
          <AIBuilderPage />
        </AdminOnly>
      }
    />,
    <Route
      key="aiBuilderCourse"
      path="learn/ai-builder/:courseId"
      element={
        <AdminOnly>
          <AIBuilderCoursePage />
        </AdminOnly>
      }
    />,
    <Route
      key="aiBuilderLesson"
      path="learn/ai-builder/:courseId/lesson/:lessonIndex"
      element={
        <AdminOnly>
          <AIBuilderLessonPage />
        </AdminOnly>
      }
    />,
  ];
}

function learnerRoutes() {
  return [
    /* Learner routes — accessible to ALL authenticated users */
    <Route key="catalog" path="learn" element={<CatalogPage />} />,
    <Route key="learnCourseDetail" path="learn/courses/:courseId" element={<CourseDetailPage />} />,
    <Route
      key="learnModule"
      path="learn/courses/:courseId/module/:moduleId"
      element={<LearnPage />}
    />,
    <Route key="learnQuiz" path="learn/courses/:courseId/quiz/:moduleId" element={<QuizPage />} />,
    <Route
      key="badgesRedirect"
      path="learn/badges"
      element={<Navigate to="/learn/certificates" replace />}
    />,
    <Route key="learn-certificates" path="learn/certificates" element={<CertificatesPage />} />,
    <Route key="profile" path="learn/profile" element={<ProfilePage />} />,
    <Route key="learnDashboard" path="learn/dashboard" element={<DashboardPage />} />,
  ];
}

export default function App() {
  // MSAL redirect landing. In the amidacore/tsrx deployment the app reg
  // registers only the ROOT url as the SPA redirect URI (all three tsrx apps
  // standardize on root — see amidacore-azure-infra 04_identity/tessarix.tf),
  // so Azure returns the auth code to "/", not "/auth/callback". Without this
  // the root route hits ProtectedRoute -> /login before AuthCallback can run,
  // dropping the code -> redirect loop. When the URL carries an MSAL redirect
  // response (?code= on success or ?error= on failure, both with &state=),
  // render AuthCallback regardless of path so it completes the token exchange.
  // On ADW, where VITE_REDIRECT_URI is /auth/callback, the callback URL still
  // carries ?code&state, so this short-circuit fires there too — it renders the
  // same AuthCallback that route would, and the stuck-spinner bug fixed below
  // affected ADW as much as amidacore.
  //
  // Originally shipped as #164 on develop only. The 2026-08-13 main<-develop
  // reconciliation resolved every conflict to main's side and silently dropped
  // it, which broke amidacore dev sign-in (11 hits on /?code= with zero
  // /auth/azure/token calls). Keep it in main so a future reconcile cannot
  // lose it again.
  //
  // The search string MUST come from useLocation(), not window.location. This
  // read is what decides whether to short-circuit, so it has to be re-evaluated
  // when the app navigates — and reading the global subscribes to nothing.
  // main.tsx mounts `<BrowserRouter><App /></BrowserRouter>`, so `<App />` is one
  // element created once; when AuthCallback's navigate() updates BrowserRouter's
  // location state, React bails out of re-rendering that identical child. App
  // then kept returning <AuthCallback /> forever: sign-in completed, the URL
  // became /dashboard, and the spinner stayed on screen until the user reloaded
  // by hand. useLocation() makes App a subscriber, so the short-circuit releases
  // as soon as the redirect params leave the URL.
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  if (params.has('state') && (params.has('code') || params.has('error'))) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/*
          Pathless layout route: the Suspense + chunk-error boundary for every
          lazy page, rendered inside Layout's <main> via its <Outlet />. One
          boundary for all routes, below the chrome, so a page chunk downloads
          behind the sidebar and header instead of replacing them.
        */}
        <Route element={<RouteOutlet />}>
          <Route index element={<RootRedirect />} />
          {adminRoutes()}
          {aiBuilderRoutes()}
          {learnerRoutes()}
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
